import React, { useEffect, useState, useCallback } from "react";
import { getData } from "../../../utils/getData";
import Matches from "../../common/football/Macthes";
import NoDataFound from "../../common/NoDataFound";
import News from "../../common/News";
import TeamSquad from "./TeamSquad";
import { loadStyles } from "../../../utils/cssLoader";

// Tab configuration for better maintainability
const TAB_CONFIG = {
  "team-squad": {
    requiresData: true,
    component: "squad",
  },
  "team-upcoming": {
    requiresData: true,
    component: "matches",
    matchType: "upcoming",
  },
  "team-results": {
    requiresData: true,
    component: "matches",
    matchType: "finished",
  },
  "team-news": {
    requiresData: true,
    component: "news",
    newsType: "team",
  },
  "team-stats": {
    requiresData: true,
    component: "stats",
  },
  "team-table": {
    requiresData: true,
    component: "table",
  },
};

const TeamPage = ({
  baseUrl,
  difference,
  sport = "football",
  dataId,
  loadCSS,
  headData,
  setDataId,
  setDataType,
  setHead,
  setLeagueId,
  websiteBaseUrl,
  dataPath = "football",
  dataClient = false,
  squadData,
}) => {
  const [active, setActive] = useState(null);
  const [activeTabData, setActiveTabData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tabs, setTabs] = useState([]);

  // Initialize tabs from headData
  useEffect(() => {
    if (headData?.tabs?.length > 0) {
      const teamTabs = headData.tabs.map((tab) => ({
        key: `team-${tab?.replaceAll("_", "-")}`,
        label: tab.replaceAll("_", " "),
      }));
      setTabs(teamTabs);
      setActive({
        key: `team-${headData.tabs[0]?.replaceAll("_", "-")}`,
        label: headData.tabs[0].replaceAll("_", " "),
      });
    }
  }, [headData]);

  // Fetch data for active tab
  const fetchData = useCallback(async () => {
    if (!active?.key) return;

    const tabConfig = TAB_CONFIG[active.key];

    // Handle squad tab separately (uses pre-loaded data)
    if (active.key === "team-squad") {
      setActiveTabData(squadData);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const params = {
        dataId,
        dataType: active.key,
        baseUrl,
        sport,
      };

      // Add newsType for news tab
      if (tabConfig?.newsType) {
        params.newsType = tabConfig.newsType;
      }

      const data = await getData(params);
      setActiveTabData(data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setActiveTabData(null);
    } finally {
      setIsLoading(false);
    }
  }, [active?.key, dataId, baseUrl, sport, squadData]);

  useEffect(() => {
    if (active) {
      fetchData();
    }
  }, [active, fetchData]);

  useEffect(() => {
    if (loadCSS === "true") {
      loadStyles("matchsummary");
    }
  }, [loadCSS]);

  // Handle tab click
  const handleTabClick = (key, label) => {
    if (active?.key !== key) {
      setIsLoading(true);
      setActive({ key, label });
      setActiveTabData(null);
    }
  };

  // Render tab content based on active tab
  const renderTabContent = () => {
    if (isLoading) {
      return <div className="loading-state"><h3>Loading...</h3></div>;
    }

    const tabConfig = TAB_CONFIG[active?.key];

    if (!tabConfig) {
      return <NoDataFound />;
    }

    switch (tabConfig.component) {
      case "squad":
        return activeTabData ? (
          <TeamSquad
            teamSquad={activeTabData}
            websiteBaseUrl={websiteBaseUrl}
            dataPath={dataPath}
            dataClient={dataClient}
          />
        ) : (
          <NoDataFound text="No squad data available" />
        );

      case "matches":
        return activeTabData?.data ? (
          <Matches
            data={activeTabData.data}
            difference={difference}
            setDataId={setDataId}
            setDataType={setDataType}
            setLeagueId={setLeagueId}
            setHead={setHead}
            matchType={tabConfig.matchType}
            websiteBaseUrl={websiteBaseUrl}
            dataPath={dataPath}
            dataClient={dataClient}
          />
        ) : (
          <NoDataFound text={`No ${tabConfig.matchType} matches found`} />
        );

      case "news":
        return activeTabData?.data ? (
          <News
            data={activeTabData.data}
            loadCSS={loadCSS}
            id={dataId}
            newsType={tabConfig.newsType}
            sport={sport}
            baseUrl={baseUrl}
          />
        ) : (
          <NoDataFound text="No news available" />
        );

      case "stats":
        return activeTabData?.data ? (
          <div className="team_stats_wrp">
            {/* Stats component would go here */}
            <pre>{JSON.stringify(activeTabData.data, null, 2)}</pre>
          </div>
        ) : (
          <NoDataFound text="No stats available" />
        );

      case "table":
        return activeTabData?.data ? (
          <div className="team_table_wrp">
            {/* Table component would go here */}
            <pre>{JSON.stringify(activeTabData.data, null, 2)}</pre>
          </div>
        ) : (
          <NoDataFound text="No table data available" />
        );

      default:
        return <NoDataFound />;
    }
  };

  return (
    <div className="match-page">
      <div className="detail_page">
        {tabs.length > 0 && (
          <nav className="inner_tab detail_main_tab">
            <ul>
              {tabs.map(({ key, label }) => (
                <li
                  key={key}
                  className={active?.key === key ? "active" : ""}
                  onClick={() => handleTabClick(key, label)}
                  data-name={key}
                >
                  <button>{label}</button>
                </li>
              ))}
            </ul>
          </nav>
        )}
        <div className="tab_content">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default TeamPage;
